import React from "react";
import { Link } from "react-router-dom";
import CcgRoutePremiumPage from "../components/CcgRoutePremiumPage";
import PostcodeQuotenew from "./PostcodeQuotenew";
import "./CcgRoutePremium.css";

const cardGrid = (className, cards) => (
  <div className={className}>
    {cards.map((card) => (
      <article className={card.className || ""} key={card.title}>
        <h3>{card.title}</h3>
        {card.body ? <p>{card.body}</p> : null}
        {card.items ? <ul>{card.items.map((item) => <li key={item}>{item}</li>)}</ul> : null}
        {card.link ? <Link to={card.link.to} className={card.link.className || "route-link-inline"}>{card.link.label}</Link> : null}
        {card.links ? (
          <div className="route-link-btn-row">
            {card.links.map((link) => <Link to={link.to} className={link.className || "route-link-btn"} key={link.to}>{link.label}</Link>)}
          </div>
        ) : null}
      </article>
    ))}
  </div>
);

const burnhamTaxiRoute = {
  id: "burnham-taxi",
  path: "/burnham-taxi",
  liveStripVariant: "slough",
  seo: {
    title: "Burnham Taxi - Fixed-Price Heathrow Transfers & Local Cabs | CheapCabGrab",
    description:
      "Book reliable Burnham taxis for Heathrow, Burnham station, Burnham Beeches, Burnham Beeches Hotel & Spa and local SL1-SL2 journeys. Fixed fares, licensed drivers and 24/7 support.",
    keywords:
      "Burnham taxi, Burnham to Heathrow taxi, Burnham station taxi, Burnham Beeches taxi, Burnham Beeches Hotel and Spa taxi, airport taxi Burnham, CheapCabGrab",
    canonical: "https://www.cheapcabgrab.com/burnham-taxi",
    ogTitle: "Burnham Taxi - Heathrow Transfers, Station Pickups and Local Cabs",
    ogDescription:
      "Fixed-price Burnham taxis for Heathrow, Burnham station, Burnham Beeches and nearby hotels with licensed drivers and online booking.",
    serviceName: "CheapCabGrab - Burnham Taxi Service",
    areaServed: ["Burnham", "Burnham Station", "Burnham Beeches", "Burnham Beeches Hotel & Spa", "Heathrow Airport"],
    offerCatalogName: "Burnham Taxi Routes",
    offerName: "Burnham fixed-price taxi service",
    offerDescription: "Fixed-price taxi transfers from Burnham to Heathrow, local stations, hotels and nearby SL postcode areas.",
    reservationName: "Taxi booking in Burnham",
  },
  hero: {
    eyebrow: "Burnham taxi service",
    h1: "Burnham Taxi - Fixed-Price Heathrow & Local Transfers",
    subtitle: "Burnham station, Burnham Beeches and Heathrow airport specialists.",
    body:
      "Book Burnham taxis with fixed upfront fares, licensed drivers and secure online booking. Ideal for Heathrow transfers, station pickups, hotel journeys and local SL postcode trips.",
  },
  quote: {
    label: "Get instant Burnham taxi quotes",
    note: "Free - Fast - No obligation",
    presetPickup: "Burnham, SL1",
    presetDropoff: "",
  },
  flags: [
    "Fixed fares agreed upfront - no meters",
    "Burnham station, hotels and Heathrow pickups",
    "Licensed drivers, luggage-ready vehicles",
    "Saloon, MPV, minibus and coach options",
  ],
  reviewPageKey: "/burnham-taxi",
  reviewAriaLabel: "Burnham taxi customer reviews",
  lhrStatusTitle: "Live Heathrow checks for Burnham airport taxis",
  floatingCta: "Book Burnham Taxi",
  mobileCta: "Book Burnham Taxi",
  supportNudge: "Need Burnham taxi help?",
  sections: [
    {
      id: "burnham-overview",
      title: "Burnham taxis at a glance",
      body: "A practical local taxi page for airport transfers, station connections, hotel pickups and Burnham Beeches visits.",
      render: () => cardGrid("route-overview-grid", [
        {
          title: "Popular pickups",
          items: [
            "Burnham station and Elizabeth line connections",
            "Burnham Beeches and nearby woodland walks",
            "Burnham Beeches Hotel & Spa",
            "Burnham village, Lent Rise, Britwell and Cippenham edge pickups",
          ],
        },
        {
          title: "Airport transfer use cases",
          items: [
            "Early Heathrow departures",
            "Hotel guests with luggage",
            "Families needing MPVs or child seats",
            "Business travel with receipts",
          ],
        },
        {
          title: "Useful linked routes",
          body: "For the dedicated airport route, use the Burnham to Heathrow page.",
          links: [
            { to: "/burnham-to-heathrow", label: "Burnham to Heathrow" },
            { to: "/slough-taxi", label: "Slough taxi", className: "route-link-btn route-link-btn-secondary" },
          ],
        },
      ]),
    },
    {
      id: "burnham-areas",
      title: "Burnham local pickup coverage",
      body: "We keep Burnham content separate from Slough because the main pickup points and journey patterns are different.",
      render: () => cardGrid("route-areas-grid", [
        {
          title: "Burnham station and local homes",
          items: [
            "Station pickups for Elizabeth line and GWR passengers",
            "Local homes around Burnham village and Lent Rise",
            "Short transfers to Slough, Taplow, Maidenhead and Windsor",
          ],
          link: { to: "/burnham-to-heathrow", label: "Book Burnham to Heathrow ->" },
        },
        {
          title: "Burnham Beeches and hotel transfers",
          items: [
            "Burnham Beeches day visits and walks",
            "Burnham Beeches Hotel & Spa pickups",
            "Airport transfers for wedding, spa and leisure stays",
          ],
          link: { to: "/heathrow-hotels", label: "Heathrow hotel guide ->" },
        },
      ]),
    },
    {
      id: "burnham-faq",
      title: "Burnham taxi - FAQs",
      body: "Common questions for Burnham local and airport taxi bookings.",
      render: () => (
        <div className="route-faq-grid">
          {[
            ["Can you collect from Burnham station?", "Yes. Burnham station is a regular pickup point for local journeys, Heathrow transfers and onward travel to hotels or nearby towns."],
            ["Do you cover Burnham Beeches Hotel & Spa?", "Yes. We can collect from Burnham Beeches Hotel & Spa for Heathrow, local dinners, events and onward journeys."],
            ["Is Burnham to Heathrow available 24/7?", "Yes. Pre-booked Burnham airport transfers are available for early flights, late arrivals and overnight travel."],
            ["Can I book a larger vehicle in Burnham?", "Yes. MPVs and minibuses are available for extra luggage, family groups and hotel bookings."],
          ].map(([title, body]) => <article className="route-faq-card" key={title}><h3>{title}</h3><p>{body}</p></article>)}
        </div>
      ),
    },
  ],
};

const BurnhamTaxi = () => (
  <CcgRoutePremiumPage route={burnhamTaxiRoute} QuoteComponent={PostcodeQuotenew} />
);

export default BurnhamTaxi;
